import React, { useMemo, useState } from 'react'
import { Combobox } from 'components/UI/combobox/combobox'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { useBrandOptions } from 'hooks/useBrandOptions'
import { ChevronDown } from 'lucide-react'

export interface BrandSelectorProps {
  value?: number | null
  onChange: (brandId: number | null) => void
  placeholder?: string
  disabled?: boolean
}

const NO_BRAND_SENTINEL = '__none__'

/**
 * Reusable brand selector component for forms.
 * Fully controlled — parent owns the value via value/onChange props.
 */
export function BrandSelector({
  value,
  onChange,
  placeholder = 'Select a brand',
  disabled = false,
}: BrandSelectorProps) {
  const [open, setOpen] = useState(false)
  const { brandOptions, isLoading } = useBrandOptions()

  const optionsWithNoBrand = useMemo(
    () => [{ label: 'No Brand', value: NO_BRAND_SENTINEL }, ...brandOptions],
    [brandOptions]
  )

  const selectedBrand = useMemo(() => {
    if (!value) return null
    return brandOptions.find((opt) => opt.value === value) ?? null
  }, [value, brandOptions])

  const handleSelect: React.Dispatch<
    React.SetStateAction<string | number | undefined>
  > = (action) => {
    const selectedValue =
      typeof action === 'function' ? action(value ?? undefined) : action

    if (
      selectedValue === undefined ||
      selectedValue === '' ||
      selectedValue === NO_BRAND_SENTINEL
    ) {
      onChange(null)
    } else {
      onChange(Number(selectedValue))
    }
    setOpen(false)
  }

  const buttonContent = useMemo(() => {
    if (isLoading && value) {
      return <span className="text-gray-500">Loading brand...</span>
    }
    if (selectedBrand) {
      return <span className="text-gray-900">{selectedBrand.label}</span>
    }
    return <span className="text-gray-400">{placeholder}</span>
  }, [isLoading, value, selectedBrand, placeholder])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {buttonContent}
          <ChevronDown size={16} className="ml-2 flex-shrink-0 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Combobox
          options={optionsWithNoBrand}
          selected={value ?? undefined}
          onSelect={handleSelect}
          searchPlaceholder="Search brands..."
          placeholder={placeholder}
          loading={isLoading}
          notFoundPlaceholder="No brands found"
          buttonLess
        />
      </PopoverContent>
    </Popover>
  )
}
